import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Building2 } from 'lucide-react';
import MainContainer from '@/components/containers/MainContainer';
import LeftPanel from '@/components/LeftPanel';
import { EmptyState } from '@/components/ui/EmptyState';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { SearchContext } from '../../project/building/Building';
import ManufacturerDetail from './components/ManufacturerDetail';
import ManufacturerModal from './components/modals/ManufacturerModal';
import {
  useManufacturerList,
  useManufacturerDetail,
  useToggleManufacturerActive,
} from './hooks/useManufacturer';

function Manufacturer() {
  const permission = useSelector((state: any) => state.auth.pagePermission);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');
  const [filter, setFilter] = useState<{ is_active?: string[] }>({});
  const [openDialog, setOpenDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const { data: list = [], isLoading } = useManufacturerList(search);
  const { data: detail } = useManufacturerDetail(selectedId);

  const toggleActive = useToggleManufacturerActive();

  const items = useMemo(() => {
    const active = filter.is_active;
    if (!active || active.length === 0) return list;
    return list.filter((item) => active.map(String).includes(String(item.is_active)));
  }, [list, filter]);

  useEffect(() => {
    if (!selectedId && items.length > 0) {
      setSelectedId(items[0].id);
    }
  }, [items, selectedId]);

  const handleToggleActive = async () => {
    if (!detail) return;
    try {
      await toggleActive.mutateAsync({ id: detail.id, isActive: detail.is_active });
    } catch {
      // error toast is handled inside the mutation
    }
  };

  return (
    <SearchContext.Provider value={{ search, filter, setSearch, setFilter }}>
      <MainContainer>
        <LeftPanel
          title="Výrobci"
          data={items}
          loading={isLoading}
          makeActive={setSelectedId}
          active={selectedId}
          addItem={() => {
            setIsEdit(false);
            setOpenDialog(true);
          }}
          onSearch={(e: any) => setSearch(e?.target?.value || '')}
          permission={permission}
          filteredValues={{
            is_active: [
              { value: 'true', title: 'Aktivní' },
              { value: 'false', title: 'Neaktivní' },
            ],
          }}
        />

        {detail ? (
          <ManufacturerDetail
            data={detail}
            onEdit={() => {
              setIsEdit(true);
              setOpenDialog(true);
            }}
            onToggleActive={handleToggleActive}
          />
        ) : isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <SpinnerOverlay />
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <EmptyState icon={<Building2 />} title="Vyberte výrobce" />
          </div>
        )}

        <ManufacturerModal
          title="Výrobce"
          visible={openDialog}
          formProps={isEdit ? detail : null}
          onSaved={setSelectedId}
          onClose={() => {
            setIsEdit(false);
            setOpenDialog(false);
          }}
        />
      </MainContainer>
    </SearchContext.Provider>
  );
}

export default Manufacturer;
